import React, { forwardRef } from "react";
import { Button } from "@mui/material";
import { fontSans } from "../../theme/tokens";

/**
 * PrimaryCTA — the canonical Marquee-Gold pill.
 *
 * The page's primary affordance: dark text inverted on a gold fill, 980px
 * pill, 200ms ease-out-quart state changes. Flat at rest; a gold-tinted
 * lift appears on hover. Focus-visible carries a two-stop ring (dark stage
 * between button and gold ring) so the indicator never collapses against
 * the gold surface.
 *
 * Mirrors the inline implementation at Home.jsx:188–230 and the smaller
 * header variant at Header.jsx:325–349 (via size="sm").
 */

const EASE = "cubic-bezier(0.25, 1, 0.5, 1)";

const SIZE_STYLES = {
  md: {
    padding: { xs: "11px 24px", md: "13px 28px" },
    fontSize: { xs: "0.9375rem", md: "1rem" },
  },
  sm: {
    padding: { xs: "7px 18px", md: "8px 20px" },
    fontSize: "0.875rem",
  },
};

const mergeSx = (base, extra) => {
  if (!extra) return base;
  if (Array.isArray(extra)) return [base, ...extra];
  return [base, extra];
};

const PrimaryCTA = forwardRef(function PrimaryCTA(
  {
    children,
    onClick,
    href,
    target,
    rel,
    endIcon,
    startIcon,
    disabled = false,
    fullWidth = false,
    type = "button",
    size = "md",
    sx,
    ...rest
  },
  ref,
) {
  const sizing = SIZE_STYLES[size] || SIZE_STYLES.md;

  const baseSx = {
    background: "#DAA520",
    color: "#0D0D0D",
    borderRadius: "980px",
    padding: sizing.padding,
    fontFamily: fontSans,
    fontSize: sizing.fontSize,
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: "-0.005em",
    textTransform: "none",
    boxShadow: "none",
    transition: `background 200ms ${EASE}, box-shadow 200ms ${EASE}`,
    "&:hover": {
      background: "#E8B432",
      boxShadow: "0 6px 20px rgba(218,165,32,0.25)",
    },
    "&:focus-visible": {
      outline: "none",
      boxShadow: "0 0 0 2px #0D0D0D, 0 0 0 4px #DAA520",
    },
    "&.Mui-disabled": {
      background: "#DAA520",
      color: "#0D0D0D",
      opacity: 0.5,
    },
  };

  return (
    <Button
      ref={ref}
      variant="contained"
      disableElevation
      disableRipple={false}
      onClick={onClick}
      href={href}
      target={target}
      rel={rel}
      startIcon={startIcon}
      endIcon={endIcon}
      disabled={disabled}
      fullWidth={fullWidth}
      type={href ? undefined : type}
      sx={mergeSx(baseSx, sx)}
      {...rest}
    >
      {children}
    </Button>
  );
});

export default PrimaryCTA;
export { PrimaryCTA };
